import React, { useEffect, useState } from 'react';
import { ConfirmModal } from '../ds/ConfirmModal';
import { statusLabel } from '../ds/StatusMap';
import { formatDate } from '../utils/dateFormat';
import { apartmentsApi } from '../services/api';
import type { ApartmentDeleteWithRecordsCheck } from '../services/api';
import { apartmentDoorMarking, apartmentNumberWithMarking } from '../utils/apartmentLabels';
import type { ApartmentLabelParts } from '../utils/apartmentLabels';

interface Props {
  /** Lokal do usunięcia; null = okno zamknięte. */
  apartmentId: string | null;
  onClose: () => void;
  /** Po udanym usunięciu — rodzic odświeża budynek i pokazuje toast. */
  onDeleted: (apartmentNumber: string, recordsDeleted: number) => void;
  /** Odmowa serwera (sprawdzenie albo usunięcie) — rodzic pokazuje powód pod wierszem lokalu. */
  onRefused: (apartmentId: string, text: string) => void;
  /**
   * E3 (migr. 071): oznaczenie na drzwiach i klatka tego lokalu z listy budynku — obok numeru CEEB, żeby
   * superadmin rozpoznał lokal tak, jak zna go z terenu („16 (kl. II, drzwi 4)"). Brak = sam numer.
   */
  labelParts?: ApartmentLabelParts | null;
}

const LISTA: React.CSSProperties = {
  margin: 'var(--space-2) 0 0 0',
  paddingLeft: 'var(--space-5)',
};

/**
 * F8 / B5 reszta (raport uwag Bronka 2026-10-19): „Usuń lokal razem z zapisem kontroli" — TYLKO superadmin.
 *
 * Lokal z pomyłkowym zapisem kontroli (np. nieobecność w lokalu dodanym przez pomyłkę) nie dawał się usunąć nigdzie.
 * O dostępności decyduje WYŁĄCZNIE serwer (`delete-with-records-check`: nic w CEEB, protokół bieżącego roku
 * niewystawiony, lokal spoza bieżącego protokołu CEEB, nic nie biegnie, tablet nie trzyma wersji lokalu). Okno
 * wypisuje dokładnie to, co zniknie (status i data każdego zapisu, liczba źródeł ciepła), a POST niesie tę samą listę
 * zapisów z ich wersją — gdy na serwerze jest inna lista albo zapis zmienił się od otwarcia okna, odmowa RECORDS_CHANGED. Odmowa = powód pod wierszem lokalu (onRefused).
 */
const ApartmentDeleteWithRecordsModal: React.FC<Props> = ({ apartmentId, onClose, onDeleted, onRefused, labelParts }) => {
  const [check, setCheck] = useState<ApartmentDeleteWithRecordsCheck | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCheck(null);
    if (!apartmentId) return undefined;
    let aktualne = true;
    apartmentsApi.deleteWithRecordsCheck(apartmentId)
      .then((c) => {
        if (!aktualne) return;
        if (!c.allowed) {
          onRefused(apartmentId, `${c.blocked?.error || 'Nie można usunąć lokalu'}.`);
          onClose();
          return;
        }
        setCheck(c);
      })
      .catch((e) => {
        if (!aktualne) return;
        onRefused(apartmentId, e instanceof Error ? e.message : 'Nie udało się sprawdzić lokalu');
        onClose();
      });
    return () => { aktualne = false; };
    // onClose/onRefused podawane inline przez rodzica — sprawdzenie tylko przy zmianie lokalu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartmentId]);

  if (!apartmentId || !check) return null;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const r = await apartmentsApi.deleteWithRecords(apartmentId, check.records);
      onDeleted(r.apartmentNumber, r.recordsDeleted);
      onClose();
    } catch (e) {
      onRefused(apartmentId, e instanceof Error ? e.message : 'Nie udało się usunąć lokalu');
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const message = (
    <div data-testid="apartment-delete-with-records-summary">
      <p style={{ margin: 0 }}>
        {apartmentDoorMarking(labelParts)
          ? `Lokal ${apartmentNumberWithMarking(check.apartmentNumber, labelParts)} w budynku ${check.address}`
          : `Lokal ${check.apartmentNumber} (${check.address})`}
        {' '}ma zapis kontroli. Zostanie usunięty na stałe razem z:
      </p>
      <ul style={LISTA}>
        {check.records.map((rec) => (
          <li key={rec.id}>
            zapisem kontroli: {statusLabel(rec.status)}{rec.date ? `, ${formatDate(rec.date)}` : ''}
          </li>
        ))}
        {check.heatSources > 0 && <li>źródłami ciepła lokalu: {check.heatSources}</li>}
      </ul>
      <p style={{ margin: 'var(--space-3) 0 0 0' }}>
        Zapis nie jest w CEEB, więc w CEEB nic się nie zmieni. Nocny import nie doda tego lokalu ponownie
        (dodanie go ręcznie w aplikacji przywraca go normalnie). Ślad zostaje w historii zmian.
      </p>
    </div>
  );

  return (
    <ConfirmModal
      open
      title="Usuń lokal razem z zapisem kontroli"
      message={message}
      confirmLabel={busy ? 'Usuwanie…' : 'Usuń lokal i zapis'}
      cancelLabel="Anuluj"
      destructive
      confirmDisabled={busy}
      onConfirm={handleConfirm}
      onCancel={onClose}
    />
  );
};

export default ApartmentDeleteWithRecordsModal;
